import { useParams } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { Icon } from '@/atoms/Icon';
import type { TagElementType } from '@/atoms/Tags/Tags';
import { Tag } from '@/atoms/Tags/Tags';
import { TagsContainer } from '@/atoms/TagsContainer/TagsContainer';
import { IconContainer } from '@/components/Atoms/IconContainer/IconContainer';
import { useTagsFilter } from '@/components/UserTags/hooks/useTagsFilter';
import { useDetectChannelId } from '@/features/Channels/hooks/useDetectChannelId';
import { useCurrentFeedName } from '@/hooks/useCurrentFeedName';
import AvatarGroup from '@/icons/avatar-group.svg?react';
import { NeedlLink } from '@/router';
import type { CommonComponentType, Filter, RouteParam } from '@/types';
import { getAppTitle } from '@/utils/getAppTitle';
import { getOrganisedTags } from '@/utils/organiseTagsToRender';

type TagsType = CommonComponentType & { tags: TagElementType[] };

export const Tags = ({ className, style, tags }: TagsType) => {
  return (
    <TagsContainer
      className={className}
      style={style}
      tags={getOrganisedTags(tags)}
    >
      {({ color, tagKey, onClick, value, source, type }) => {
        const tag = (
          <Tag
            color={color}
            tagKey={tagKey}
            onClick={onClick}
            value={value}
            icon={getTagIcon(type)}
          />
        );

        if (type === 'user_tags' || type === 'shared_tags') {
          return (
            <TagsNeedlLink
              value={value as string}
              source={source as string}
              type={type}
            >
              {tag}
            </TagsNeedlLink>
          );
        }

        return tag;
      }}
    </TagsContainer>
  );
};

const getTagIcon = (type: TagElementType['type']) => {
  return type === 'shared_tags' ? (
    <IconContainer className='pl-1'>
      <Icon size='xs' icon={<AvatarGroup />} />
    </IconContainer>
  ) : undefined;
};

const TagsNeedlLink: React.FunctionComponent<TagsNeedlLinkType> = ({
  value,
  children,
  source,
  type,
}) => {
  const { channelId } = useDetectChannelId();
  const { subCategory } = useParams<RouteParam>();

  const feedName = useCurrentFeedName();

  const { data: facets } = useTagsFilter({
    ...(type === 'shared_tags' && { channelId }),
  });

  const pathname =
    type === 'user_tags' ? '/needlbox/tags/all' : `/channels/${channelId}/tags`;

  const filter = facets?.['Tags']?.find(
    (filter: Filter) => filter['key'] === value
  );

  const state = filter
    ? {
        filtersFromUI: {
          tags: {
            Tags: [filter],
          },
        },
      }
    : null;

  return (
    <NeedlLink
      to={{ pathname, state }}
      onClick={() => {
        if (type === 'shared_tags') {
          trackEvent('sharedtag_clicked', {
            ...(feedName && { feed_name: feedName }),
            ...(subCategory && { tab_name: subCategory }),
          });
        } else {
          trackEvent('tag_clicked', { source: getAppTitle(source ?? '') });
        }
      }}
    >
      {children}
    </NeedlLink>
  );
};

type TagsNeedlLinkType = {
  value: string;
  children: React.ReactNode;
  source: string;
  type: string;
};
